// components/MembershipTracking.jsx
import React, { useState, useEffect } from "react";
import api from "../api/axiosInstance";
import { toast } from "react-hot-toast";

const MembershipTracking = () => {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [email, setEmail] = useState("");
  const [referenceId, setReferenceId] = useState("");
  const [trackingMethod, setTrackingMethod] = useState("trackingNumber");
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if there's a tracking number in URL hash
    const hash = window.location.hash;
    if (hash === "#tracking") {
      setTrackingMethod("trackingNumber");
      // Try to get tracking number from localStorage if user previously tracked
      const savedTracking = localStorage.getItem("lastTrackingNumber");
      if (savedTracking) {
        setTrackingNumber(savedTracking);
      }
    }
  }, []);

  const handleTrackApplication = async (e) => {
    e.preventDefault();

    if (!trackingNumber && !email && !referenceId) {
      toast.error("Please enter your tracking number, email, or reference ID");
      return;
    }

    setLoading(true);
    setTrackingData(null);
    setShowDetails(false);

    try {
      const params = {};
      if (trackingMethod === "trackingNumber" && trackingNumber) {
        params.trackingNumber = trackingNumber.trim();
      } else if (trackingMethod === "email" && email) {
        params.email = email.trim();
      } else if (trackingMethod === "referenceId" && referenceId) {
        params.referenceId = referenceId.trim();
      }

      const response = await api.get("/member/tracking/track", { params });

      if (response.data.success) {
        setTrackingData(response.data.data);
        setShowDetails(true);

        // Save tracking number for future reference
        if (response.data.data.trackingNumber) {
          localStorage.setItem(
            "lastTrackingNumber",
            response.data.data.trackingNumber
          );
        }

        toast.success("Application tracking information loaded successfully!");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error tracking application:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to track application. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status.includes("Approved") || status === "Active Member") {
      return "bg-green-100 text-green-800";
    } else if (
      status.includes("Pending") ||
      status.includes("Under Approval")
    ) {
      return "bg-yellow-100 text-yellow-800";
    } else if (status.includes("Rejected")) {
      return "bg-red-100 text-red-800";
    } else if (status.includes("On Hold")) {
      return "bg-orange-100 text-orange-800";
    }
    return "bg-gray-100 text-gray-800";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Calculate progress percentage based on current step (4 steps, each 25%)
  const calculateProgressPercentage = (trackingData) => {
    if (!trackingData || !trackingData.currentStep) return 0;

    const stepOrder = {
      MEMBER_SECRETARY: 1,
      GOLF_CAPTAIN: 2,
      VICE_PRESIDENT: 3,
      PRESIDENT: 4,
    };

    const currentStep = trackingData.currentStep;

    // If current step is "COMPLETED", return 100%
    if (currentStep === "COMPLETED") {
      return 100;
    }

    const stepNumber = stepOrder[currentStep] || 0;

    // Calculate percentage (each step is 25% for 4 main steps)
    // MEMBER_SECRETARY = 25%, GOLF_CAPTAIN = 50%, etc.
    const percentage = Math.min(stepNumber * 25, 100);

    // If we have backend progress percentage, use it (might be more accurate)
    return trackingData.progressPercentage || percentage;
  };

  // Calculate step status based on current step (excluding COMPLETED from display)
  const calculateStepStatus = (step, allSteps, currentStep) => {
    // Filter out COMPLETED step from display but keep it for calculations
    const displaySteps = allSteps
      ? allSteps.filter((s) => s.step !== "COMPLETED")
      : [];

    if (!displaySteps.length || !currentStep) {
      return {
        status: step.status,
        isCurrent: step.isCurrent,
        statusText: step.status,
      };
    }

    const stepOrder = [
      "MEMBER_SECRETARY",
      "GOLF_CAPTAIN",
      "VICE_PRESIDENT",
      "PRESIDENT",
    ];

    // Handle COMPLETED as the final state
    if (currentStep === "COMPLETED") {
      // All steps should be marked as completed
      if (stepOrder.includes(step.step)) {
        return {
          status: "COMPLETED",
          isCurrent: false,
          statusText: "COMPLETED",
        };
      }
      // For COMPLETED step itself, don't display it
      return null;
    }

    const currentStepIndex = stepOrder.indexOf(currentStep);
    const stepIndex = stepOrder.indexOf(step.step);

    if (stepIndex === -1 || currentStepIndex === -1) {
      return {
        status: step.status,
        isCurrent: step.isCurrent,
        statusText: step.status,
      };
    }

    // If step is before current step, mark as completed
    if (stepIndex < currentStepIndex) {
      return {
        status: "COMPLETED",
        isCurrent: false,
        statusText: "COMPLETED",
      };
    }
    // If this is the current step
    else if (stepIndex === currentStepIndex) {
      return {
        status: step.status === "APPROVED" ? "APPROVED" : "IN_PROGRESS",
        isCurrent: true,
        statusText: step.status === "APPROVED" ? "APPROVED" : "IN PROGRESS",
      };
    }
    // If step is after current step
    else {
      // Check if backend already marked it as current
      if (step.isCurrent) {
        return {
          status: "IN_PROGRESS",
          isCurrent: true,
          statusText: "IN PROGRESS",
        };
      }
      return {
        status: "PENDING",
        isCurrent: false,
        statusText: "PENDING",
      };
    }
  };

  // Get status display color for step
  const getStepStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
      case "APPROVED":
        return "bg-green-100 text-green-800 border-green-200";
      case "REJECTED":
        return "bg-red-100 text-red-800 border-red-200";
      case "IN_PROGRESS":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Get step indicator color
  const getStepIndicatorColor = (status) => {
    switch (status) {
      case "COMPLETED":
      case "APPROVED":
        return "bg-green-500";
      case "REJECTED":
        return "bg-red-500";
      case "IN_PROGRESS":
        return "bg-blue-500 animate-pulse";
      case "PENDING":
        return "bg-gray-300";
      default:
        return "bg-gray-300";
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
      {/* Header */}
      <div className="flex items-center mb-8">
        <div className="p-3 bg-blue-100 rounded-full mr-4">
          <i className="fas fa-search-location text-blue-600 text-xl"></i>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            Track Your Application
          </h2>
          <p className="text-gray-600">
            Check the status of your RGCC membership application
          </p>
        </div>
      </div>

      {/* Tracking Form */}
      <div className="mb-10">
        <form onSubmit={handleTrackApplication} className="space-y-6">
          {/* Tracking Method Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              How would you like to track your application?
            </label>
            <div className="flex flex-wrap gap-3">
              {[
                {
                  id: "trackingNumber",
                  label: "Tracking Number",
                  icon: "hashtag",
                },
                { id: "email", label: "Email Address", icon: "envelope" },
                { id: "referenceId", label: "Reference ID", icon: "id-card" },
              ].map((method) => (
                <button
                  key={method.id}
                  type="button"
                  onClick={() => {
                    setTrackingMethod(method.id);
                    setTrackingNumber("");
                    setEmail("");
                    setReferenceId("");
                  }}
                  className={`flex items-center px-4 py-2 rounded-lg border-2 transition-colors ${
                    trackingMethod === method.id
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <i className={`fas fa-${method.icon} mr-2`}></i>
                  {method.label}
                </button>
              ))}
            </div>
          </div>

          {/* Input Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {trackingMethod === "trackingNumber" && "Tracking Number"}
              {trackingMethod === "email" && "Email Address"}
              {trackingMethod === "referenceId" && "Application Reference ID"}
            </label>
            <div className="flex flex-col sm:flex-row">
              <input
                type={trackingMethod === "email" ? "email" : "text"}
                value={
                  trackingMethod === "trackingNumber"
                    ? trackingNumber
                    : trackingMethod === "email"
                    ? email
                    : referenceId
                }
                onChange={(e) => {
                  if (trackingMethod === "trackingNumber")
                    setTrackingNumber(e.target.value);
                  if (trackingMethod === "email") setEmail(e.target.value);
                  if (trackingMethod === "referenceId")
                    setReferenceId(e.target.value);
                }}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-t-lg sm:rounded-tr-none sm:rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={
                  trackingMethod === "trackingNumber"
                    ? "Enter your tracking number (e.g., RGCC-XXXXXXX)"
                    : trackingMethod === "email"
                    ? "Enter your registered email address"
                    : "Enter your application reference ID"
                }
                required
              />
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-blue-600 text-white font-medium rounded-b-lg sm:rounded-bl-none sm:rounded-r-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Tracking...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search mr-2"></i>
                    <span className="hidden sm:inline">Track Application</span>
                    <span className="sm:hidden">Track</span>
                  </>
                )}
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              {trackingMethod === "trackingNumber" &&
                "You received this in your confirmation email"}
              {trackingMethod === "email" &&
                "Use the email you used during registration"}
              {trackingMethod === "referenceId" &&
                "Found in your application confirmation"}
            </p>
          </div>
        </form>
      </div>

      {/* Tracking Results */}
      {trackingData && (
        <div className="space-y-8">
          {/* Application Summary */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div className="mb-4 md:mb-0">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {trackingData.memberName}
                </h3>
                <div className="space-y-1">
                  <p className="text-gray-600">
                    <i className="fas fa-hashtag mr-2"></i>
                    Tracking:{" "}
                    <span className="font-mono font-bold">
                      {trackingData.trackingNumber}
                    </span>
                  </p>
                  <p className="text-gray-600">
                    <i className="fas fa-id-card mr-2"></i>
                    Reference:{" "}
                    <span className="font-medium">
                      {trackingData.applicationReferenceId}
                    </span>
                  </p>
                  <p className="text-gray-600">
                    <i className="fas fa-calendar mr-2"></i>
                    Applied:{" "}
                    <span className="font-medium">
                      {formatDate(trackingData.applicationDate)}
                    </span>
                  </p>
                </div>
              </div>

              <div className="text-center md:text-right">
                <div className="mb-2">
                  <span
                    className={`px-4 py-2 rounded-full font-medium ${getStatusColor(
                      trackingData.status
                    )}`}
                  >
                    {trackingData.status}
                  </span>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {calculateProgressPercentage(trackingData)}%
                </div>
                <div className="text-sm text-gray-600">Complete</div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>0%</span>
                <span>Application Progress</span>
                <span>100%</span>
              </div>
              <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-400 to-blue-500 rounded-full transition-all duration-1000 ease-out"
                  style={{
                    width: `${calculateProgressPercentage(trackingData)}%`,
                  }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Submitted</span>
                <span>{trackingData.currentStepStatus || "Not Started"}</span>
                <span>Completed</span>
              </div>
            </div>
          </div>

          {/* Approval Steps Timeline */}
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              <i className="fas fa-list-ol mr-3"></i>
              Approval Process Timeline
            </h3>

            <div className="space-y-4">
              {trackingData.approvalSteps &&
                trackingData.approvalSteps
                  .filter((step) => step.step !== "COMPLETED") // Filter out COMPLETED step
                  .map((step, index, filteredSteps) => {
                    const stepStatus = calculateStepStatus(
                      step,
                      trackingData.approvalSteps,
                      trackingData.currentStep
                    );

                    // Don't render if stepStatus is null (COMPLETED step)
                    if (!stepStatus) return null;

                    return (
                      <div
                        key={index}
                        className={`relative pl-10 pb-6 ${
                          index < filteredSteps.length - 1
                            ? "border-l-2 border-gray-300"
                            : ""
                        }`}
                      >
                        {/* Step Indicator */}
                        <div
                          className={`absolute left-[-9px] top-0 w-5 h-5 rounded-full border-4 border-white flex items-center justify-center ${getStepIndicatorColor(
                            stepStatus.status
                          )}`}
                        >
                          {stepStatus.status === "COMPLETED" && (
                            <i className="fas fa-check text-white text-xs"></i>
                          )}
                          {stepStatus.status === "APPROVED" && (
                            <i className="fas fa-check text-white text-xs"></i>
                          )}
                          {stepStatus.status === "REJECTED" && (
                            <i className="fas fa-times text-white text-xs"></i>
                          )}
                          {stepStatus.status === "IN_PROGRESS" && (
                            <i className="fas fa-clock text-white text-xs"></i>
                          )}
                          {stepStatus.status === "PENDING" &&
                            stepStatus.isCurrent && (
                              <i className="fas fa-clock text-white text-xs"></i>
                            )}
                        </div>

                        {/* Step Content */}
                        <div
                          className={`p-4 rounded-lg border ${getStepStatusColor(
                            stepStatus.status
                          )}`}
                        >
                          <div className="flex flex-col md:flex-row md:items-center justify-between">
                            <div>
                              <div className="flex items-center mb-2">
                                <h4 className="text-lg font-semibold text-gray-800 mr-3">
                                  Step {step.sequenceOrder}: {step.stepName}
                                </h4>
                                {stepStatus.isCurrent && (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                                    Current Step
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-600 mb-3">
                                {step.description}
                              </p>

                              {/* Show appropriate message based on status */}
                              {stepStatus.status === "PENDING" &&
                                !stepStatus.isCurrent && (
                                  <div className="flex items-center text-sm text-gray-500 mb-2">
                                    <i className="fas fa-clock mr-2"></i>
                                    Awaiting review
                                  </div>
                                )}

                              {stepStatus.status === "IN_PROGRESS" && (
                                <div className="flex items-center text-sm text-blue-600 mb-2">
                                  <i className="fas fa-spinner fa-spin mr-2"></i>
                                  Currently being reviewed
                                </div>
                              )}

                              {step.completedDate && (
                                <div className="flex items-center text-sm text-gray-500 mb-2">
                                  <i className="fas fa-calendar-check mr-2"></i>
                                  {formatDate(step.completedDate)}
                                </div>
                              )}

                              {step.completedBy && (
                                <div className="flex items-center text-sm text-gray-500">
                                  <i className="fas fa-user-check mr-2"></i>
                                  Approved by: {step.completedBy}
                                </div>
                              )}

                              {step.comments && (
                                <div className="mt-3 p-3 bg-white rounded border text-sm">
                                  <span className="font-medium text-gray-700">
                                    Comments:
                                  </span>{" "}
                                  {step.comments}
                                </div>
                              )}
                            </div>

                            <div className="mt-3 md:mt-0 md:ml-4">
                              <span
                                className={`px-3 py-1 rounded-full text-sm font-medium ${
                                  stepStatus.status === "COMPLETED" ||
                                  stepStatus.status === "APPROVED"
                                    ? "bg-green-100 text-green-800"
                                    : stepStatus.status === "REJECTED"
                                    ? "bg-red-100 text-red-800"
                                    : stepStatus.status === "IN_PROGRESS"
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {stepStatus.statusText}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
            </div>

            {/* Completion Message (when all steps are done) */}
            {trackingData.currentStep === "COMPLETED" && (
              <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-full mr-4">
                    <i className="fas fa-check-circle text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-800 mb-1">
                      Approval Process Complete! 🎉
                    </h4>
                    <p className="text-gray-700">
                      All approval steps have been completed. Your application
                      has been fully approved.
                      {trackingData.estimatedCompletionDate && (
                        <span className="block mt-1 text-gray-600">
                          <i className="fas fa-calendar-check mr-1"></i>
                          Completed on: {trackingData.estimatedCompletionDate}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Next Step Information (only show when not completed) */}
            {trackingData.currentStep &&
              trackingData.currentStep !== "COMPLETED" && (
                <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                  <div className="flex items-center">
                    <div className="p-3 bg-green-100 rounded-full mr-4">
                      <i className="fas fa-arrow-right text-green-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-800 mb-1">
                        Next Step
                      </h4>
                      <p className="text-gray-700">
                        Your application is now at{" "}
                        <strong>{trackingData.currentStepStatus}</strong> stage.
                        {trackingData.estimatedCompletionDate && (
                          <span className="block mt-1 text-gray-600">
                            <i className="fas fa-clock mr-1"></i>
                            Estimated completion:{" "}
                            {trackingData.estimatedCompletionDate}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
          </div>

          {/* History Log - Collapsible */}
          <div>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center justify-between w-full p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <span className="font-medium text-gray-800">
                <i className="fas fa-history mr-2"></i>
                Application History Log
              </span>
              <i
                className={`fas fa-chevron-${
                  showDetails ? "up" : "down"
                } text-gray-500`}
              ></i>
            </button>

            {showDetails && trackingData.history && (
              <div className="mt-4 space-y-4 max-h-96 overflow-y-auto p-4 border rounded-lg">
                {trackingData.history.map((entry, index) => (
                  <div
                    key={index}
                    className="pb-4 border-b border-gray-200 last:border-0"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h5 className="font-medium text-gray-800">
                          {entry.action}
                        </h5>
                        <p className="text-gray-600 text-sm mt-1">
                          {entry.description}
                        </p>
                        {entry.notes && (
                          <p className="text-gray-500 text-sm mt-2 italic">
                            "{entry.notes}"
                          </p>
                        )}
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        <div>{formatDate(entry.dateTime)}</div>
                        {/* {entry.performedBy && (
                          <div className="mt-1">By: {entry.performedBy}</div>
                        )} */}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contact Information */}
          <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
            <h4 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
              <i className="fas fa-question-circle mr-2"></i>
              Need Assistance?
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-gray-700 mb-2">
                  If you have questions about your application status or need
                  help:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <i className="fas fa-envelope mr-2 text-blue-500"></i>
                    Email: rgcc66div@gmail.com
                  </li>
                  <li className="flex items-center">
                    <i className="fas fa-phone mr-2 text-blue-500"></i>
                    Phone: +88 01766693142
                  </li>
                  <li className="flex items-center">
                    <i className="fas fa-clock mr-2 text-blue-500"></i>
                    Hours: 9:00 AM - 5:00 PM (Sun - Thu)
                  </li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded-lg border">
                <h5 className="font-medium text-gray-800 mb-2">Quick Tips</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Save your tracking number for future reference</li>
                  <li>• Check your email regularly for updates</li>
                  <li>
                    • Each approval step typically takes 2-3 business days
                  </li>
                  <li>• You'll receive email notifications at each step</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!trackingData && !loading && (
        <div className="text-center py-12">
          <div className="w-24 h-24 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
            <i className="fas fa-search text-blue-500 text-3xl"></i>
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-3">
            Track Your Application
          </h3>
          <p className="text-gray-600 max-w-md mx-auto mb-6">
            Enter your tracking number, email, or reference ID above to check
            the status of your RGCC membership application.
          </p>
          <div className="grid md:grid-cols-3 gap-4 max-w-2xl mx-auto">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-hashtag text-green-600"></i>
              </div>
              <h4 className="font-medium text-gray-800 mb-1">
                Use Tracking Number
              </h4>
              <p className="text-sm text-gray-600">
                Found in your confirmation email
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-envelope text-blue-600"></i>
              </div>
              <h4 className="font-medium text-gray-800 mb-1">
                Use Email Address
              </h4>
              <p className="text-sm text-gray-600">
                The email you registered with
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-id-card text-purple-600"></i>
              </div>
              <h4 className="font-medium text-gray-800 mb-1">
                Use Reference ID
              </h4>
              <p className="text-sm text-gray-600">
                Application reference number
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Tracking your application...</p>
        </div>
      )}
    </div>
  );
};

export default MembershipTracking;
